<template>
  <main class="login-page">
    <section class="login-intro" aria-labelledby="login-intro-title">
      <BrandLogo class="brand" variant="zh-primary" tone="current" />
      <div class="login-intro__content">
        <h1 id="login-intro-title">平台管理后台</h1>
        <p>管理员身份与普通用户空间严格隔离。</p>
      </div>
      <div class="login-intro__footer">
        <span>LINGXI WORKSHOP · ADMIN CONSOLE</span>
        <span class="login-intro__security">
          <i class="ri-shield-keyhole-line" aria-hidden="true" />
          独立身份域
        </span>
      </div>
    </section>
    <section class="login-panel" aria-labelledby="login-form-title">
      <a-card class="login-card" :bordered="false">
        <header class="login-card__header">
          <div class="login-card__icon" aria-hidden="true">
            <i class="ri-lock-2-line" />
          </div>
          <div>
            <h2 id="login-form-title">管理员登录</h2>
            <p>请输入已初始化的后台账号。</p>
          </div>
        </header>

        <a-form
          :model="form"
          layout="vertical"
          aria-label="管理员登录表单"
          :aria-busy="loading"
          @finish="login"
        >
          <a-form-item
            label="邮箱"
            name="email"
            :rules="[{ required: true, type: 'email', message: '请输入有效邮箱' }]"
          >
            <a-input
              v-model:value="form.email"
              type="email"
              inputmode="email"
              autocomplete="username"
              placeholder="name@example.com"
              size="large"
            >
              <template #prefix><i class="ri-mail-line" aria-hidden="true" /></template>
            </a-input>
          </a-form-item>
          <a-form-item
            label="密码"
            name="password"
            :rules="[{ required: true, message: '请输入密码' }]"
          >
            <a-input-password
              v-model:value="form.password"
              autocomplete="current-password"
              placeholder="输入密码"
              size="large"
            >
              <template #prefix><i class="ri-key-2-line" aria-hidden="true" /></template>
            </a-input-password>
          </a-form-item>

          <section v-if="showCaptcha" class="captcha-challenge" aria-label="安全验证">
            <p><i class="ri-shield-check-line" aria-hidden="true" />安全验证</p>
            <altcha-widget
              :key="captchaKey"
              challengeurl="/api/v1/auth/captcha/challenge?scene=admin-login"
              auto="off"
              language="zh-cn"
              hidelogo
              hidefooter
              @verified="captchaVerified"
              @statechange="captchaStateChanged"
            />
            <small>完成验证后即可继续登录</small>
          </section>

          <div aria-live="polite">
            <a-alert v-if="error" type="error" :message="error" show-icon />
          </div>
          <a-button html-type="submit" type="primary" block size="large" :loading="loading">
            安全登录
          </a-button>
        </a-form>

        <footer class="login-card__footer">
          <i class="ri-information-line" aria-hidden="true" />
          仅限已授权的平台管理员访问
        </footer>
      </a-card>
    </section>
  </main>
</template>

<script setup lang="ts">
import { reactive, ref } from 'vue';
import 'altcha';
import 'altcha/i18n/zh-cn';
import { useRoute, useRouter } from 'vue-router';
import { adminApi, setAdminToken } from '@/api';
import BrandLogo from '@/components/BrandLogo.vue';
const router = useRouter();
const route = useRoute();
const form = reactive({ email: '', password: '' });
const loading = ref(false);
const error = ref('');
const showCaptcha = ref(false);
const captchaPayload = ref<string | undefined>();
const captchaKey = ref(0);
async function login() {
  loading.value = true;
  error.value = '';
  try {
    const result = await adminApi.login(form.email, form.password, captchaPayload.value);
    setAdminToken(result.accessToken);
    const redirect =
      typeof route.query.redirect === 'string' ? route.query.redirect : { name: 'admin-dashboard' };
    await router.replace(redirect);
  } catch (e) {
    const captchaWasSubmitted = Boolean(captchaPayload.value);
    const response = (e as { response?: { data?: { code?: string; message?: string } } }).response;
    error.value = response?.data?.message ?? '登录失败，请检查账号与服务状态';
    if (response?.data?.code === 'CAPTCHA_REQUIRED') showCaptcha.value = true;
    if (showCaptcha.value && captchaWasSubmitted) {
      captchaPayload.value = undefined;
      captchaKey.value += 1;
    }
  } finally {
    loading.value = false;
  }
}
function captchaStateChanged(event: Event): void {
  if ((event as AltchaStateChangeEvent).detail.state !== 'verified')
    captchaPayload.value = undefined;
}
function captchaVerified(event: Event): void {
  captchaPayload.value = (event as AltchaVerifiedEvent).detail.payload;
  if (error.value === '请先完成安全验证') error.value = '';
}
</script>

<style scoped>
.captcha-challenge {
  margin: 0 0 20px;
  padding: 16px;
  border: 1px solid var(--aw-border);
  border-radius: var(--aw-radius-lg);
  background: var(--aw-surface-subtle);
}

.captcha-challenge p {
  display: flex;
  gap: 8px;
  align-items: center;
  margin: 0 0 10px;
  color: var(--aw-text);
  font-weight: 700;
}

.captcha-challenge p i {
  color: var(--aw-primary);
  font-size: 18px;
}

.captcha-challenge small {
  display: block;
  margin-top: 8px;
  color: var(--aw-text-secondary);
}

.captcha-challenge :deep(altcha-widget) {
  display: block;
  --altcha-border-radius: 10px;
  --altcha-color-base: var(--aw-surface);
  --altcha-color-border: var(--aw-border);
  --altcha-color-text: var(--aw-text);
  --altcha-color-border-focus: var(--aw-primary);
}
</style>
